/**
 * Phase 41 Plan 02 — Admin Cron Jobs overview page.
 *
 * Locked decisions implemented:
 *  - D-07: flat sortable 7-column table (Name | Description | Queue | Cron | Next run | Last run | Actions)
 *  - D-08: default sort by nextRunAt asc; sortable: name, queue, nextRun
 *  - D-03: P2 stacked Last-run cell (Scheduled + Manual sub-rows)
 *  - D-04: 1-line red error snippet under failed timestamp, clickable to Bull Board
 *  - D-05: TanStack Query auto-poll 15s (hook-level)
 *  - D-06: instant-fire Run-now with toast, no confirm dialog
 *  - D-11: ↗ Bull Board link = /admin/queues/queue/<queueName>
 *  - D-13: — placeholder for never-run sub-rows
 *  - D-02a: Unknown scheduler rows greyed, Run-now disabled
 *  - D-02b: Not registered rows greyed, but Run-now stays enabled —
 *    one-shot manual runs work via SCHEDULER_DESCRIPTIONS even when no
 *    recurring scheduler is registered (lets admins UAT crons on staging).
 *
 * Security:
 *  - T-03: error snippets render via React {snippet} text nodes only; never
 *    via raw-HTML injection. React auto-escapes string content.
 *  - T-05: Run-now button is disabled while runMutation.isPending (TanStack
 *    Query's natural UI debounce).
 */
import { useState, useMemo } from 'react';
import { useCronJobs, useRunCronJob } from '../../hooks/useCronJobs';
import { AppToast } from '../../components/app-access/AppToast';
import type { AppToastState } from '../../components/app-access/AppToast';
import { Spinner } from '../../components/common/Spinner';
import { CronJobEditModal } from '../../components/admin/CronJobEditModal';
import type { CronJobRow, LastRunSummary } from '../../services/api/cronJobs';
import { formatDisplayDateTime } from '../../utils/format';

type SortKey = 'name' | 'queue' | 'nextRun';
type SortDir = 'asc' | 'desc';

const QUEUE_CHIP_CLASSES: Record<string, string> = {
  'maintenance-jobs': 'bg-blue-100 text-blue-800',
  'extra-cost-jobs': 'bg-green-100 text-green-800',
  'engineering-metrics': 'bg-purple-100 text-purple-800',
  'timesheet-jobs': 'bg-amber-100 text-amber-800',
};

function formatDateTime(iso: string): string {
  // MKS-351 — dd.mm.yyyy HH:mm from the shared util.
  return formatDisplayDateTime(iso);
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '';
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
}

/**
 * D-03 sub-row renderer: timestamp + duration + ok/fail badge, and on failure
 * a 1-line red error snippet clickable to Bull Board.
 *
 * T-03: errorSnippet rendered as a JSX text node — React auto-escapes the
 * string content. NEVER use raw-HTML injection here.
 */
function LastRunSubRow({
  label,
  summary,
  bullBoardHref,
}: {
  label: 'Scheduled' | 'Manual';
  summary: LastRunSummary | null;
  bullBoardHref: string;
}) {
  if (!summary) {
    return (
      <div className="text-xs text-gray-400">
        {label} · <span>—</span>
      </div>
    );
  }
  const badgeClasses = summary.ok ? 'text-green-700' : 'text-red-700';
  const duration = formatDuration(summary.durationMs);
  return (
    <div className="text-xs">
      <div className={badgeClasses}>
        {label} · {formatDateTime(summary.finishedAt)}{' '}
        ({summary.ok ? 'ok' : 'fail'}
        {duration ? `, ${duration}` : ''})
      </div>
      {!summary.ok && summary.errorSnippet && (
        <a
          href={bullBoardHref}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-red-600 text-xs truncate max-w-[24rem] hover:underline"
          title="Open in Bull Board for full stack trace"
        >
          {summary.errorSnippet}
        </a>
      )}
    </div>
  );
}

function QueueChip({ queueName }: { queueName: string }) {
  const classes = QUEUE_CHIP_CLASSES[queueName] ?? 'bg-gray-100 text-gray-800';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {queueName}
    </span>
  );
}

export default function CronJobsPage() {
  const { data: rows, isLoading, error } = useCronJobs();
  const runMutation = useRunCronJob();

  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'nextRun', // D-08 default
    dir: 'asc',
  });
  const [toast, setToast] = useState<AppToastState | null>(null);
  const [editingRow, setEditingRow] = useState<CronJobRow | null>(null);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const sortedRows = useMemo(() => {
    const list = [...(rows ?? [])];
    const mult = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.key) {
        case 'name':
          return mult * a.schedulerId.localeCompare(b.schedulerId);
        case 'queue':
          return mult * a.queueName.localeCompare(b.queueName);
        case 'nextRun': {
          // null sorts last regardless of direction (never-firing schedulers go to the bottom)
          const aMs = a.nextRunAt ? new Date(a.nextRunAt).getTime() : Number.POSITIVE_INFINITY;
          const bMs = b.nextRunAt ? new Date(b.nextRunAt).getTime() : Number.POSITIVE_INFINITY;
          if (aMs === bMs) return 0;
          if (aMs === Number.POSITIVE_INFINITY) return 1;
          if (bMs === Number.POSITIVE_INFINITY) return -1;
          return mult * (aMs - bMs);
        }
        default:
          return 0;
      }
    });
    return list;
  }, [rows, sort]);

  const handleRunNow = async (row: CronJobRow) => {
    try {
      await runMutation.mutateAsync(row.schedulerId);
      setToast({ message: `Job enqueued: ${row.schedulerId}`, variant: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to enqueue job',
        variant: 'error',
      });
    }
  };

  const headerCell = (label: string, key: SortKey | null) => {
    if (!key) {
      return (
        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">{label}</th>
      );
    }
    const active = sort.key === key;
    const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
    return (
      <th
        className="px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer select-none hover:bg-gray-50"
        onClick={() => toggleSort(key)}
      >
        {label}
        {arrow}
      </th>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700">
        Failed to load cron jobs: {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Cron Jobs</h1>
        <p className="text-sm text-gray-600">
          Scheduled BullMQ jobs registered by the worker process. Manual runs are audited under{' '}
          <code>cron_job_manual_run</code>.
        </p>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headerCell('Name', 'name')}
              {headerCell('Description', null)}
              {headerCell('Queue', 'queue')}
              {headerCell('Cron pattern', null)}
              {headerCell('Next run', 'nextRun')}
              {headerCell('Last run', null)}
              {headerCell('Actions', null)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRows.map((row) => {
              const greyed = !row.isRegistered || !row.hasDescription;
              const rowClasses = greyed ? 'bg-gray-50 text-gray-400' : '';
              const bullBoardHref = `/admin/queues/queue/${encodeURIComponent(row.queueName)}`;
              const description =
                row.description ?? (row.hasDescription ? '' : 'Unknown scheduler');
              // D-02a Unknown scheduler: Run-now disabled (!hasDescription) — no job name to fire.
              // D-02b Not registered (staging case, D-14): Run-now stays enabled —
              // SCHEDULER_DESCRIPTIONS holds the queue/jobName/defaultData needed
              // to enqueue a one-off, even when the recurring scheduler is skipped.
              // T-05: also disabled while a mutation is in-flight.
              const runDisabled = !row.hasDescription || runMutation.isPending;
              return (
                <tr key={row.schedulerId} className={rowClasses}>
                  <td className="px-3 py-2 text-sm font-medium">{row.schedulerId}</td>
                  <td className="px-3 py-2 text-sm">
                    {description}
                    {!row.isRegistered && (
                      <span className="ml-2 text-xs text-gray-500 italic">(Not registered)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <QueueChip queueName={row.queueName} />
                  </td>
                  <td className="px-3 py-2 text-sm font-mono">
                    <span className={row.disabled ? 'text-gray-400 line-through' : ''}>
                      {row.pattern ?? '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {row.disabled ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                        Disabled
                      </span>
                    ) : (
                      row.nextRunAt ? formatDateTime(row.nextRunAt) : '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div className="space-y-0.5">
                      <LastRunSubRow
                        label="Scheduled"
                        summary={row.lastScheduled}
                        bullBoardHref={bullBoardHref}
                      />
                      <LastRunSubRow
                        label="Manual"
                        summary={row.lastManual}
                        bullBoardHref={bullBoardHref}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    {row.hasDescription ? (
                      <button
                        type="button"
                        onClick={() => handleRunNow(row)}
                        disabled={runDisabled}
                        className="text-sm px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          row.isRegistered
                            ? 'Enqueue a manual one-off run'
                            : 'Enqueue a manual one-off run (no recurring schedule — staging or drift)'
                        }
                      >
                        Run now
                      </button>
                    ) : (
                      // D-02a Unknown scheduler — disabled Run-now (per truth list)
                      <button
                        type="button"
                        disabled
                        className="text-sm px-2 py-1 rounded bg-primary-600 text-white opacity-50 cursor-not-allowed"
                        title="Cannot run an unknown scheduler — no job name registered"
                      >
                        Run now
                      </button>
                    )}
                    {' '}
                    <a
                      href={bullBoardHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline ml-2"
                    >
                      ↗ Bull Board
                    </a>
                    {row.hasDescription && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => setEditingRow(row)}
                          className="text-sm px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 ml-2"
                          title="Edit schedule or disable"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AppToast toast={toast} onDismiss={() => setToast(null)} />

      {editingRow && (
        <CronJobEditModal
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={() => {
            setToast({ message: `Saved: ${editingRow.schedulerId}`, variant: 'success' });
            setEditingRow(null);
          }}
        />
      )}
    </div>
  );
}
